import React, { useState, useMemo, forwardRef } from 'react';
import {
  TableCell,
  TableRow,
  Menu,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { IconButton, Select } from '@coolremote/shared';
import { MoreVert, LinkOff, ArrowForwardIos, CheckOutlined, Edit, Check, Close } from '@material-ui/icons';
import clsx from 'clsx';
import { t } from 'ttag';
import { BacnetDevice as bacnetDeviceSdk } from 'coolremote-sdk';
import { useStoreActions } from '@models/RootStore';
import { useConfirmation } from '@coolremote/shared';
import useStyles from './styles';
import AddUnitDialog from '../AddUnitDialog';

/**
 * DeviceTableRow renders a collapsible device-level row in the BACnet detection table.
 * Shows device info and aggregated unit data. Expands to show unit rows.
 *
 * When `singleUnit` is provided, the row is rendered as a single combined row
 * showing both device info and the unit's data inline (no expand needed).
 */
const DeviceTableRow = forwardRef((props, ref) => {
  const classes = useStyles();

  const {
    device,
    unitCount,
    boundCount,
    expanded,
    onToggleExpand,
    caDeviceSerial,
    caDeviceLine,
    isDisconnected,
    onUnitCreated,
    onDeviceDeleted,
    highlightRow,
    // Single unit inline props (provided when unitCount === 1)
    singleUnit,
    singleUnitStatus,
    handleConnectionChange,
    handleParamTemplateAssign,
    handleParamTemplateUnassign,
    handleUiTemplateAssign,
    handleUiTemplateUnassign,
    goToUiTemplateMapping,
    goToParamTemplateMapping,
    onDeleteUnit,
    updateBacNetUnit,
    siteBacNetParameterTemplates,
    uiTemplates,
    statusColors,
    statusLabels,
    onMenuClick,
  } = props;

  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState(null);
  const [isAddUnitDialogOpen, setIsAddUnitDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const confirm = useConfirmation();
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const { deleteBacnetUnit, updateBacnetDevice } = useStoreActions((action) => action.bacnetDevices);

  const filteredUiTemplates = useMemo(() => {
    if (!uiTemplates || !singleUnit) return [];
    return uiTemplates.filter((uiTemplate) => uiTemplate.paramTemplate == singleUnit.paramTemplate);
  }, [uiTemplates, singleUnit]);

  const handleClick = (event) => {
    setActionsMenuAnchorEl(event.currentTarget);
    onMenuClick?.(singleUnit?.id ?? device?.id);
  };
  const handleClose = () => {
    setActionsMenuAnchorEl(null);
  };

  const handleAddAppUnit = () => {
    handleClose();
    setIsAddUnitDialogOpen(true);
  };

  const handleRemoveDevice = async () => {
    handleClose();
    const confirmed = await confirm({
      title: t`Remove Device`,
      bodyMessage: t`NOTE: The Device will be removed from BACnet Devices Mapping`,
      confirmText: t`OK`,
      cancelText: t`Cancel`,
    });
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    try {
      if (isDisconnected) {
        await bacnetDeviceSdk.deleteBacnetDevice(device.id);
      } else {
        await updateBacnetDevice({
          id: device.id,
          data: { isUserSelected: false },
        });
      }
      if (onDeviceDeleted) {
        onDeviceDeleted();
      }
    } catch (error) {
      addMessage({ message: error.message, hideReportButton: true });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddUnitDialogClose = (createdUnit) => {
    setIsAddUnitDialogOpen(false);
    if (createdUnit && onUnitCreated) onUnitCreated(createdUnit);
  };

  // --- Inline single-unit handlers ---

  const handleTitleEditStart = () => {
    setEditTitleValue(singleUnit?.title ?? '');
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    updateBacNetUnit({ id: singleUnit.id, data: { title: editTitleValue } });
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditTitleValue(singleUnit?.title ?? '');
    setIsEditingTitle(false);
  };

  const handleConnect = async () => {
    const result = await confirm({
      title: t`Connect Device?`,
      bodyMessage: t`Are you sure you want to connect this device?`,
      confirmText: t`Connect`,
      cancelText: t`Cancel`,
    });
    if (result) {
      handleConnectionChange(singleUnit?.id, true);
      handleClose();
    }
  };

  const handleDisconnect = async () => {
    const result = await confirm({
      title: t`Disconnect Device?`,
      bodyMessage: t`Are you sure you want to disconnect this device?`,
      confirmText: t`Disconnect`,
      cancelText: t`Cancel`,
    });
    if (result) {
      handleConnectionChange(singleUnit?.id, false);
      handleClose();
    }
  };

  const handleDeleteUnit = async () => {
    const result = await confirm({
      title: t`Delete Unit?`,
      bodyMessage: t`Are you sure you want to delete this unit?`,
      confirmText: t`Delete`,
      cancelText: t`Cancel`,
    });
    if (result) {
      await deleteBacnetUnit(singleUnit?.id);
      onDeleteUnit(singleUnit?.id);
      handleClose();
    }
  };

  const handleGoToParamTemplateMapping = () => {
    goToParamTemplateMapping(singleUnit.id, singleUnit.paramTemplate);
    handleClose();
  };

  const handleGoToUiTemplateMapping = () => {
    goToUiTemplateMapping(singleUnit.uiTemplate ?? null, singleUnit.id, singleUnit.paramTemplate);
    handleClose();
  };

  const handleParamTemplateChange = async (templateId) => {
    try {
      if (templateId) {
        await handleParamTemplateAssign(templateId, singleUnit.id);
      } else {
        await handleParamTemplateUnassign(singleUnit?.paramTemplate, singleUnit.id);
      }
    } catch (error) {
      addMessage({ message: error.message, hideReportButton: true });
    }
  };

  const handleUiTemplateChange = async (templateId) => {
    try {
      if (templateId) {
        await handleUiTemplateAssign(templateId, singleUnit.id);
      } else {
        await handleUiTemplateUnassign(singleUnit?.uiTemplate, singleUnit.id);
      }
    } catch (error) {
      addMessage({ message: error.message, hideReportButton: true });
    }
  };

  const renderParamTemplateSelect = () => {
    if (!singleUnit.compatibleParamTemplates?.length) return '--';
    return (
      <Select
        menuPortal
        classes={{ trigger: classes.selectTrigger }}
        maxMenuWidth={400} 
        value={singleUnit.paramTemplate || null}
        onChange={handleParamTemplateChange}
        disabled={singleUnit.isEngaged}
        className={classes.inputRoot}
        options={[
          { value: null, label: '--', disabled: !singleUnit.paramTemplate },
          ...(singleUnit.compatibleParamTemplates?.map(({ id, name }) => ({
            value: id,
            label: name,
          })) || [])
        ]}
      />
    );
  };

  const renderUiTemplatesSelect = () => {
    if (!filteredUiTemplates.length) return '--';
    return (
      <Select
        menuPortal
        value={singleUnit.uiTemplate || null}
        onChange={handleUiTemplateChange}
        disabled={singleUnit.isEngaged || !singleUnit.paramTemplate}
        className={classes.inputRoot}
        classes={{ trigger: classes.selectTrigger }}
        maxMenuWidth={400} 
        options={[
          { value: null, label: '--', disabled: !singleUnit.uiTemplate },
          ...filteredUiTemplates.map(({ id, name }) => ({
            value: id,
            label: name,
          }))
        ]}
      />
    );
  };

  const getParamTemplateUniversalMapping = () => {
    const templateId = singleUnit?.paramTemplate;
    if (!templateId) return false;
    const template = siteBacNetParameterTemplates?.[templateId];
    return !!template?.entityTemplate;
  };

  const isActionsMenuOpen = !!actionsMenuAnchorEl;

  if (!device) return null;

  const bindStatusText = boundCount > 0 ? `${t`Bound`} (${boundCount})` : t`Not Bound`;
  const statusColor = singleUnit ? (statusColors?.[singleUnitStatus] || '#545964') : undefined;
  const statusLabel = singleUnit ? (statusLabels?.[singleUnitStatus]?.() || '') : bindStatusText;

  return (
    <>
      <TableRow
        ref={ref}
        className={clsx(
          classes.deviceRow,
          { [classes.disconnectedDevice]: isDisconnected },
          { [classes.highlight]: highlightRow },
        )}
        onClick={singleUnit ? undefined : onToggleExpand}
        style={{ cursor: singleUnit ? 'default' : 'pointer' }}
      >
        {/* Expand arrow — hidden when a single unit is shown inline */}
        <TableCell classes={{ root: classes.arrowCell }} align="center">
          {!singleUnit && (
            <ArrowForwardIos
              className={clsx(classes.expandArrow, { [classes.expandArrowOpen]: expanded })}
            />
          )}
        </TableCell>

        {/* Device Name (ID) */}
        <TableCell
          title={`${t`Device Name`}: ${device.name}`}
          style={{ cursor: 'help' }}
          classes={{ root: clsx(classes.TableCell) }}
          align="left"
        >
          <div className={classes.nameCell}>
            <div className={classes.nameColumn}>
              <div className={classes.nameText}>{device.name}</div>
              <div className={classes.idText}>({device.bacnetId ?? '<?>'})</div>
            </div>
            {isDisconnected && <LinkOff style={{ color: 'red', marginLeft: '10px' }} />}
          </div>
        </TableCell>

        <TableCell
          title={`${t`Vendor Name`}: ${device.vendorName}`}
          style={{ cursor: 'help' }}
          classes={{ root: clsx(classes.TableCell) }}
          align="left"
        >
          <div className={classes.vendorColumn}>
            <div className={classes.vendorNameText}>{device.vendorName}</div>
            <div className={classes.vendorIdText}>({device.vendorId})</div>
          </div>
        </TableCell>

        <TableCell
          title={`${t`Model`}: ${device.modelName}`}
          style={{ cursor: 'help' }}
          classes={{ root: clsx(classes.TableCell) }}
          align="left"
        >
          <div className={classes.modelText}>{device.modelName}</div>
        </TableCell>

        {/* MAC Addr */}
        <TableCell classes={{ root: clsx(classes.TableCell) }} align="left">
          {device.macAddress}
        </TableCell>

        <TableCell
          title={`${t`CA Device SN`}: ${caDeviceSerial || ''} (${caDeviceLine})`}
          style={{ cursor: 'help' }}
          classes={{ root: clsx(classes.TableCell) }}
          align="left"
        >
          <div className={classes.caColumn}>
            {caDeviceSerial ? (
              <>
                <div className={classes.caSerialText}>{caDeviceSerial}</div>
                <div className={classes.caLineText}>({caDeviceLine})</div>
              </>
            ) : (
              <div className={classes.caSerialText}>{caDeviceLine}</div>
            )}
          </div>
        </TableCell>

        {/* App Unit */}
        <TableCell
          classes={{ root: clsx(classes.TableCell, singleUnit && classes.appUnitNameCell) }}
          align="left"
        >
          {singleUnit ? (
            isEditingTitle ? (
              <div className={classes.editContainer} onClick={(e) => e.stopPropagation()}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={editTitleValue}
                  onChange={(e) => setEditTitleValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave();
                    if (e.key === 'Escape') handleTitleCancel();
                  }}
                  InputProps={{ classes: { input: classes.editInput } }}
                />
                <IconButton size="small" onClick={handleTitleSave} className={classes.editAction}>
                  <Check fontSize="small" style={{ color: '#5FBA82' }} />
                </IconButton>
                <IconButton size="small" onClick={handleTitleCancel} className={classes.editAction}>
                  <Close fontSize="small" style={{ color: '#F05146' }} />
                </IconButton>
              </div>
            ) : (
              <div className={classes.viewContainer}>
                <span className={classes.titleText} title={singleUnit.title}>{singleUnit.title || '--'}</span>
                <IconButton size="small" onClick={handleTitleEditStart} className={classes.editIcon}>
                  <Edit />
                </IconButton>
              </div>
            )
          ) : (
            <span className={classes.aggregationText}>
              {unitCount > 0 ? `${unitCount} ${t`App Units`}` : ''}
            </span>
          )}
        </TableCell>

        {/* Mapping */}
        <TableCell
          classes={{ root: clsx(classes.TableCell, singleUnit && classes.mediumWidth) }}
          align="left"
        >
          {singleUnit ? renderParamTemplateSelect() : '--'}
        </TableCell>

        {/* Control UI */}
        <TableCell
          classes={{ root: clsx(classes.TableCell, singleUnit && classes.mediumWidth) }}
          align="left"
        >
          {singleUnit ? renderUiTemplatesSelect() : '--'}
        </TableCell>

        {/* Universal */}
        <TableCell classes={{ root: clsx(classes.TableCell) }} align="center">
          {singleUnit && getParamTemplateUniversalMapping() ? <CheckOutlined /> : null}
        </TableCell>

        {/* Status */}
        <TableCell
          classes={{ root: clsx(classes.TableCell, classes.statusCell) }}
          align="center"
          style={singleUnit ? { color: statusColor } : undefined}
        >
          {statusLabel}
        </TableCell>

        {/* Actions menu */}
        <TableCell
          classes={{ root: classes.TableCell }}
          align="left"
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            id="device-actions-menu-button"
            aria-controls={isActionsMenuOpen ? 'device-actions-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isActionsMenuOpen ? 'true' : undefined}
            onClick={handleClick}
          >
            <MoreVert />
          </IconButton>
          <Menu
            id="device-actions-menu"
            anchorEl={actionsMenuAnchorEl}
            open={isActionsMenuOpen}
            onClose={handleClose}
            MenuListProps={{ 'aria-labelledby': 'device-actions-menu-button' }}
          >
            {singleUnit && [
              <MenuItem
                key="connect"
                disabled={singleUnit.isEngaged || !singleUnit.paramTemplate}
                onClick={handleConnect}
              >{t`Bind`}</MenuItem>,
              <MenuItem
                key="disconnect"
                disabled={!singleUnit.isEngaged}
                onClick={handleDisconnect}
              >{t`Unbind`}</MenuItem>,
              <MenuItem key="param-template-mapping" onClick={handleGoToParamTemplateMapping}>
                {t`Object Mapping`}
              </MenuItem>,
              <MenuItem key="ui-template-mapping" onClick={handleGoToUiTemplateMapping}>
                {t`Control UI`}
              </MenuItem>,
              <MenuItem key="sep1" disabled className={classes.menuItemWithLine}>
                <hr style={{ width: '100%', border: 'none', borderTop: '1px solid #ccc' }} />
              </MenuItem>,
            ]}
            <MenuItem key="add-app-unit" onClick={handleAddAppUnit}>{t`+ App Unit`}</MenuItem>
            <MenuItem
              key="remove-device"
              onClick={handleRemoveDevice}
              disabled={boundCount > 0 || !!singleUnit?.isEngaged}
              style={(boundCount > 0 || singleUnit?.isEngaged) ? { opacity: 0.5 } : { color: '#F05146' }}
            >
              {isDeleting ? t`Removing...` : t`Remove Device`}
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      {isAddUnitDialogOpen && (
        <AddUnitDialog
          open={isAddUnitDialogOpen}
          onClose={handleAddUnitDialogClose}
          bacnetDeviceId={device.id}
          deviceName={device.name}
        />
      )}
    </>
  );
});

DeviceTableRow.displayName = 'DeviceTableRow';

export default DeviceTableRow;
